import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ContentWrapper } from './BookingQuiz.styled';
import { OptionsList } from './components/OptionsList';
import { Question } from './components/Question';
import { QuizResult } from './components/QuizResult';
import { ServiceTypeCards } from './components/ServiceTypeCards';
import { Title } from './components/Title';
import { AuthFlowLayout } from 'components/AuthFlowLayout/AuthFlowLayout';
import { Loader } from 'components/Loader/Loader';
import { usePatient } from 'hooks/usePatient/usePatient';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { Navigation } from 'screens/Book/components/Navigation/Navigation';
import { ProgressTracker } from 'screens/Book/components/ProgressTracker/ProgressTracker';
import { analytics } from 'utilities/analytics';
import { pageUrl } from 'utilities/pageUrl';
import { useBreakpoints } from 'utilities/useBreakpoints';

import { QUIZ_ANSWER_QUERY, QUIZ_QUESTION_QUERY } from './BookingQuiz.graphql';
import { BookingQuizAnswer, BookingQuizQuestion } from './BookingQuiz.types';

export const BookingQuiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = usePatient();
  const params = new URLSearchParams(location.search);
  const currentQParam = params.get('q');
  const currentAParam = params.get('a');

  const { isDesktop } = useBreakpoints();

  const [parentBookingQuizAnswerId, setParentBookingQuizAnswerId] = useState<string | null>(
    currentQParam || null
  );
  const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(currentAParam || null);
  const { data } = useQuery<{
    bookingQuizQuestions: BookingQuizQuestion;
  }>(QUIZ_QUESTION_QUERY, {
    variables: {
      parentBookingQuizAnswerId
    },
    onError: error => {
      BugTracker.notify(error, 'Could not fetch quiz question');
    }
  });
  const { data: selectedAnswerData, loading: selectedAnswerLoading } = useQuery<{
    bookingQuizAnswer?: BookingQuizAnswer;
  }>(QUIZ_ANSWER_QUERY, {
    variables: {
      id: Number(selectedAnswerId)
    },
    skip: !selectedAnswerId,
    onError: error => {
      BugTracker.notify(error, 'Could not fetch quiz answer');
    }
  });

  const currentQuestion = data?.bookingQuizQuestions;
  const appointmentTypes = selectedAnswerData?.bookingQuizAnswer?.appointmentTypes || [];

  const category = selectedAnswerData?.bookingQuizAnswer?.category;

  // Check if we should show the ServiceTypeCards (initial category selection)
  const isInitialQuestion = !currentQuestion?.parentBookingQuizAnswerId;
  const shouldShowServiceTypeCards =
    isInitialQuestion &&
    currentQuestion?.childBookingQuizAnswers?.some(
      answer => answer.category === 'fertility' || answer.category === 'wellness'
    );

  function handleResponse(quizAnswer: BookingQuizAnswer) {
    analytics.track(analytics.EVENTS.APPOINTMENT_QUIZ_RESPONDED, {
      appointment_category: currentQuestion?.category,
      question_text: currentQuestion?.text,
      question_id: currentQuestion?.id,
      option_text_selected: quizAnswer.text,
      option_id_selected: quizAnswer.id
    });
    if (quizAnswer.appointmentTypeIds.length > 0) {
      navigate(
        pageUrl.booking.bookingQuiz({
          type: 'answer',
          id: quizAnswer.id
        })
      );
    } else {
      navigate(
        pageUrl.booking.bookingQuiz({
          type: 'question',
          id: quizAnswer.id
        })
      );
    }
  }

  useEffect(() => {
    analytics.page(analytics.PAGES.APPOINTMENT_BOOKING_QUIZ);
  }, []);

  useEffect(() => {
    if (`${parentBookingQuizAnswerId}` !== currentQParam) {
      setParentBookingQuizAnswerId(currentQParam || null);
    }
  }, [parentBookingQuizAnswerId, currentQParam]);

  useEffect(() => {
    if (`${selectedAnswerId}` !== currentAParam) {
      setSelectedAnswerId(currentAParam || null);
    }
  }, [selectedAnswerId, currentAParam]);

  const renderHeaderContent = (): string => {
    if (appointmentTypes.length > 0) return 'We recommend...';

    if (currentQuestion?.category) {
      return currentQuestion.category.charAt(0).toUpperCase() + currentQuestion.category.slice(1);
    }

    return '';
  };

  return (
    <AuthFlowLayout
      title={null}
      renderMainContent={() => {
        if (selectedAnswerLoading) return <Loader container />;
        return (
          <ContentWrapper>
            {!isDesktop && (
              <>
                <ProgressTracker step={renderHeaderContent()} />
                <Title text={renderHeaderContent()} />
              </>
            )}
            {appointmentTypes.length > 0 ? (
              <QuizResult
                appointmentTypes={appointmentTypes}
                category={category}
                isLoggedIn={isLoggedIn}
              />
            ) : (
              currentQuestion && (
                <>
                  <Question text={currentQuestion.text} category={currentQuestion.category} />
                  {shouldShowServiceTypeCards ? (
                    <ServiceTypeCards
                      onFertilityClick={() => {
                        const fertilityAnswer = currentQuestion.childBookingQuizAnswers.find(
                          answer => answer.category === 'fertility'
                        );
                        if (fertilityAnswer) {
                          handleResponse(fertilityAnswer);
                        }
                      }}
                      onHolisticClick={() => {
                        const wellnessAnswer = currentQuestion.childBookingQuizAnswers.find(
                          answer => answer.category === 'wellness'
                        );
                        if (wellnessAnswer) {
                          handleResponse(wellnessAnswer);
                        }
                      }}
                    />
                  ) : (
                    <OptionsList
                      options={currentQuestion.childBookingQuizAnswers}
                      large={!currentQuestion.parentBookingQuizAnswerId}
                      onSelect={handleResponse}
                      isLoggedIn={isLoggedIn}
                    />
                  )}
                </>
              )
            )}
            <Navigation step={renderHeaderContent()} />
          </ContentWrapper>
        );
      }}
    />
  );
};
